import React from 'react';
import { Box, TextField, Button } from '@mui/material';

/**
 * Reusable candidate IDs input with a multiline text field and an "Upload .txt" button.
 * The .txt file is parsed: lines/commas/whitespace split, numeric tokens kept,
 * and joined with newlines (one ID per line, matching the candidates.txt format).
 *
 * Props:
 *   value        - current text value
 *   onChange      - setter for the text value
 *   label        - TextField label (default: "Candidate IDs")
 *   placeholder  - TextField placeholder
 *   helperText   - TextField helper text
 *   size         - TextField size (default: "small")
 *   fullWidth    - whether TextField fills width (default: true)
 *   minRows      - min rows (default: 2)
 *   maxRows      - max rows (default: 8)
 *   sx           - extra styles for the root Box
 */
const CandidateIdsInput = ({
  value,
  onChange,
  label = 'Candidate IDs',
  placeholder = 'One ID per line, e.g.\n77887\n77888\n78382',
  helperText = 'One ID per line, or upload a .txt file. Leave empty for all candidates.',
  size = 'small',
  fullWidth = true,
  minRows = 2,
  maxRows = 8,
  sx,
}) => {
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const ids = text.split(/[\n,\s]+/).map(id => id.trim()).filter(id => id && !isNaN(id));
      onChange(ids.join('\n'));
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', ...sx }}>
      <TextField
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        fullWidth={fullWidth}
        size={size}
        helperText={helperText}
        multiline
        minRows={minRows}
        maxRows={maxRows}
      />
      <Button variant="outlined" component="label" size="small" sx={{ height: 40, minWidth: 'max-content' }}>
        Upload .txt
        <input type="file" hidden accept=".txt" onChange={handleFileUpload} />
      </Button>
    </Box>
  );
};

export default CandidateIdsInput;